import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import queryString from 'query-string';
import moment from 'moment';

import { makeStyles } from '@material-ui/core/styles';
import { useTheme } from '@material-ui/core/styles';

import { handleSortChange } from 'modules/common';
import { openrtbTargetStatistics, openrtbTargetGraph } from 'lib/api/openrtb';
import {
  makeRowColumn,
  getHeadObj,
  cenversionTpCode,
  dayColor,
  dataConverter,
} from 'lib/commonLib';

import NewButton from 'components/molecules/NewButton';
import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import SortLabelWrap from 'components/molecules/MbTable/SortLabelWrap';
import BarGraph from 'components/organisms/Chart/BarGraph';
import NewCommonFilter from 'components/organisms/NewCommonFilter';

import RtbItlTpCodeFilter from './RtbItlTpCodeFilter';

const useStyles = makeStyles((theme) => ({
  sortWrap: { textAlign: 'right' },
}));

const TargetStatistics = (props) => {
  const { location } = props;
  const { tp_code, isPop } = queryString.parse(location.search);
  const { allProductCodeList } = useSelector(({ common }) => ({
    allProductCodeList: common.allProductCodeList,
  }));

  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();

  let initParams = {
    order: 'STATS_DTTM',
    sort: 'desc',
    sDate: moment().format('YYYYMM01'),
    eDate: moment().format('YYYYMMDD'),
    itlTpCode: !!tp_code ? tp_code : '04',
    pltfomTpCode: 'all',
  };
  const initCustomized = {
    STATS_DTTM: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'STATS_DTTM');
        let result;
        if (rowObj['FIXED'] === 0) {
          result = t('합계');
        } else {
          if (!!rowObj['PLTFOM_TP_CODE']) {
            result = cenversionTpCode(rowObj['PLTFOM_TP_CODE']);
          } else {
            result = dayColor(
              rowObj['STATS_DTTM'],
              bodyItem1.type,
              bodyItem1.format
            );
          }
        }
        return result;
      },
    },
  };

  let convertKeys = [
    'sDate',
    'eDate',
    'itlTpCode',
    'advrtsPrdtCode',
    'advrtsTpCode',
    'pltfomTpCode',
    'mediaScriptNo',
  ];
  const newInitParams = dataConverter(
    queryString.parse(location.search),
    convertKeys
  );

  initParams = { ...initParams, ...newInitParams };

  const [initialization, setInitialization] = useState(false);
  const [params, setParams] = useState(initParams);
  const [tempParams, setTempParams] = useState(initParams);
  const [customized, setCustomized] = useState(initCustomized);
  const [usedTpCodes, setUsedTpCodes] = useState([]);

  const graphList = [
    {
      title: t('지출금액'),
      data: { bar: ['ADVRTS_AMT'] },
      yAxisData: { left: { ADVRTS_AMT: true }, right: {} },
      referenceLine: {
        ADVRTS_AMT: {
          type: 'average',
          label: t('지출금액'),
          visibleValue: true,
        },
      },
    },
    {
      title: t('노출'),
      data: { bar: ['PAR_EPRS_CNT'] },
      yAxisData: { left: { PAR_EPRS_CNT: true }, right: {} },
      referenceLine: {
        PAR_EPRS_CNT: {
          type: 'average',
          label: t('노출'),
          visibleValue: true,
        },
      },
    },
    {
      title: 'CTR',
      data: { bar: ['CLICK_RATE'] },
      yAxisData: { left: { CLICK_RATE: true, isPercent: true }, right: {} },
      customizeData: {
        CLICK_RATE: { tooltipFormat: '0.000', isPercent: true },
      },
      referenceLine: {
        CLICK_RATE: { type: 'average', label: 'CTR', visibleValue: true },
      },
    },
    {
      title: 'ECPM',
      data: { bar: ['ECPM'] },
      yAxisData: { left: { ECPM: true }, right: {} },
      referenceLine: {
        ECPM: { type: 'average', label: 'ECPM', visibleValue: true },
      },
    },
    {
      title: t('상품 점유율'),
      data: {
        line: [
          'CW_PAR_EPRS_RATE',
          'PF_PAR_EPRS_RATE',
          'RC_PAR_EPRS_RATE',
          'TC_PAR_EPRS_RATE',
          'TR_PAR_EPRS_RATE',
          'TV_PAR_EPRS_RATE',
          'UM_PAR_EPRS_RATE',
          'WC_PAR_EPRS_RATE',
          'WV_PAR_EPRS_RATE',
        ],
      },
      yAxisData: {
        left: { TC_PAR_EPRS_RATE: true, isPercent: true },
        right: {},
      },
      customizeData: {
        CW_PAR_EPRS_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.CW,
        },
        PF_PAR_EPRS_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.PF,
        },
        RC_PAR_EPRS_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.RC,
        },
        TC_PAR_EPRS_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.TC,
        },
        TR_PAR_EPRS_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.TR,
        },
        TV_PAR_EPRS_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.TV,
        },
        UM_PAR_EPRS_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.UM,
        },
        WC_PAR_EPRS_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.WC,
        },
        WV_PAR_EPRS_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.WV,
        },
      },
    },
    {
      title: t('상품 수익률'),
      data: {
        line: [
          'CW_MEDIA_PYMNT_AMT_RATE',
          'PF_MEDIA_PYMNT_AMT_RATE',
          'RC_MEDIA_PYMNT_AMT_RATE',
          'TC_MEDIA_PYMNT_AMT_RATE',
          'TR_MEDIA_PYMNT_AMT_RATE',
          'TV_MEDIA_PYMNT_AMT_RATE',
          'UM_MEDIA_PYMNT_AMT_RATE',
          'WC_MEDIA_PYMNT_AMT_RATE',
          'WV_MEDIA_PYMNT_AMT_RATE',
        ],
      },
      yAxisData: {
        left: { TC_MEDIA_PYMNT_AMT_RATE: true, isPercent: true },
        right: {},
      },
      customizeData: {
        CW_MEDIA_PYMNT_AMT_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.CW,
        },
        PF_MEDIA_PYMNT_AMT_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.PF,
        },
        RC_MEDIA_PYMNT_AMT_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.RC,
        },
        TC_MEDIA_PYMNT_AMT_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.TC,
        },
        TR_MEDIA_PYMNT_AMT_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.TR,
        },
        TV_MEDIA_PYMNT_AMT_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.TV,
        },
        UM_MEDIA_PYMNT_AMT_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.UM,
        },
        WC_MEDIA_PYMNT_AMT_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.WC,
        },
        WV_MEDIA_PYMNT_AMT_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
          color: theme.palette.graph.key.WV,
        },
      },
    },
  ];

  useEffect(() => {
    if (usedTpCodes.length === 0 && allProductCodeList.size > 0) {
      let result = [];
      allProductCodeList.forEach((target) => {
        result.push({ code: target['codeVal'], name: target['codeDesc'] });
      });
      setUsedTpCodes(result);
    } else if (usedTpCodes.length > 0) {
      handleCustom(params);
    }
    // eslint-disable-next-line
  }, [allProductCodeList, usedTpCodes]);

  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  // header 및 body 컬럼 커스텀
  const handleCustom = (newParams = params) => {
    let newCustomized = { ...initCustomized };
    for (let i = 0; usedTpCodes.length > i; i++) {
      newCustomized[usedTpCodes[i].code + '_PAR_EPRS_CNT'] = {
        makeHead: (headsMap) => {
          return (
            <SortLabelWrap
              params={newParams}
              label={usedTpCodes[i].name}
              orderKey={usedTpCodes[i].code + '_PAR_EPRS_CNT'}
              onClick={(e) =>
                getParam(
                  handleSortChange(
                    newParams,
                    usedTpCodes[i].code + '_PAR_EPRS_CNT'
                  )
                )
              }
            />
          );
        },
        makeBody: (rowObj) => {
          return (
            <div className={classes.sortWrap}>
              <div title={t('구좌노출수')}>
                {makeRowColumn(
                  rowObj[usedTpCodes[i].code + '_PAR_EPRS_CNT'],
                  'number',
                  '#,##0'
                )}
                (
                {makeRowColumn(
                  rowObj[usedTpCodes[i].code + '_PAR_EPRS_RATE'],
                  'number',
                  '0.00%'
                )}
                )
              </div>
              <div className="green" title="ECPM">
                {makeRowColumn(
                  rowObj[usedTpCodes[i].code + '_ECPM'],
                  'number',
                  '#,##0'
                )}
              </div>
              <div title="수익율">
                {makeRowColumn(
                  rowObj[usedTpCodes[i].code + '_MEDIA_PYMNT_AMT_RATE'],
                  'number',
                  '0.000%'
                )}
              </div>
              <div className="red" title={t('지출금액')}>
                {makeRowColumn(
                  rowObj[usedTpCodes[i].code + '_ADVRTS_AMT'],
                  'number',
                  '#,##0'
                )}
              </div>
              <div title={t('평균지출')}>
                {makeRowColumn(
                  rowObj[usedTpCodes[i].code + '_AVG_ADVRTS_AMT'],
                  'number',
                  '#,##0.#'
                )}
              </div>
              <div className="blue" title={t('클릭률')}>
                {makeRowColumn(
                  rowObj[usedTpCodes[i].code + '_CLICK_RATE'],
                  'number',
                  '0.000%'
                )}
              </div>
            </div>
          );
        },
      };
      newCustomized[usedTpCodes[i].code + '_ECPM'] = { merged: true };
      newCustomized[usedTpCodes[i].code + '_MEDIA_PYMNT_AMT_RATE'] = {
        merged: true,
      };
      newCustomized[usedTpCodes[i].code + '_PAR_EPRS_RATE'] = { merged: true };
      newCustomized[usedTpCodes[i].code + '_ADVRTS_AMT'] = { merged: true };
      newCustomized[usedTpCodes[i].code + '_AVG_ADVRTS_AMT'] = { merged: true };
      newCustomized[usedTpCodes[i].code + '_CLICK_RATE'] = { merged: true };
    }
    setCustomized(newCustomized);
  };

  const handleOnReset = () => {
    if (
      JSON.stringify(params) !== JSON.stringify(initParams) ||
      JSON.stringify(tempParams) !== JSON.stringify(initParams)
    ) {
      setInitialization(true);
      setParams(initParams);
      setTempParams(initParams);
      handleCustom(initParams);
    }
  };

  const getParam = () => {
    if (JSON.stringify(params) !== JSON.stringify(tempParams)) {
      setParams(tempParams);
      handleCustom(tempParams);
    }
  };

  const getTableParam = (newParams, name) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
      setTempParams(newParams);
      handleCustom(newParams);
    }
  };

  const getTempParam = (newParams) => {
    if (JSON.stringify(tempParams) !== JSON.stringify(newParams)) {
      setTempParams(newParams);
    }
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          params={tempParams}
          getParam={getTempParam}
          useExternalFilter={false}
          pltfomTpCodeDetailFilter={['04']}
        />
        <RtbItlTpCodeFilter params={tempParams} getTempParam={getTempParam} />
        <NewButton type="filterButton" onClick={getParam} disableRipple>
          {t('검색')}
        </NewButton>
      </NewFilterWrap>
      {!isPop && (
        <BarGraph
          params={params}
          dataReqPromise={openrtbTargetGraph}
          graphList={graphList}
          title={t('타게팅분포 데이터 그래프')}
          labelType="MMDD"
        />
      )}
      {usedTpCodes.length > 0 && (
        <NewTableContainer
          params={params}
          dataReqPromise={openrtbTargetStatistics}
          getParam={getTableParam}
          customized={customized}
          useSearch={false}
          isIncludingTotal
        />
      )}
    </>
  );
};

export default withRouter(TargetStatistics);
